import { IFormField } from '@opengov/forms-library/types';

import {
  STATE_ARCHIVED,
  STATE_DRAFT,
  STATE_INTERNAL_PUBLISHED,
  STATE_PUBLISHED,
  STATE_UNPUBLISHED
} from '../constants';

export interface IUser {
  _id: string;
  email: string;
  name: {
    first: string;
    last: string;
  };
  og_id: number;
}

export type IStoryState =
  | typeof STATE_PUBLISHED
  | typeof STATE_INTERNAL_PUBLISHED
  | typeof STATE_DRAFT
  | typeof STATE_ARCHIVED;

export type IPublicationState = typeof STATE_PUBLISHED | typeof STATE_DRAFT | typeof STATE_UNPUBLISHED;

export interface IStory {
  _id: string;
  author: IUser;
  createdAt: string;
  createdBy: string | IUser;
  embeds?: any[];
  entity_id: number;
  folderId?: string;
  state: IStoryState;
  story_key: string;
  title: string;
  updatedAt: string;
  updatedBy: string | IUser;
  visibility?: string;
  description?: string;
  meta?: any; // object
  public_url?: string;
  public_url_aws?: string;
  publishedAt?: string;
  publishedBy?: string | IUser;
  thumbnail?: string;
  thumbnail_relative_path?: string;
  pdf?: any; //blob
  paddingTop?: number;
  paddingBottom?: number;
  customCssCode?: string;
  deletedAt?: string;
  isFollowable?: boolean;
  folder: {
    publicationId: string;
    id: string;
    title: string;
    publication?: IPublication;
  };
}

export enum EventStatus {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  COMPLETE = 'Complete'
}

export interface EventRow {
  id?: string;
  title: string;
  description: string;
  status: {
    id: string;
    title: string;
  };
}

export interface IProposalStoryContent {
  content: Object;
  format: string;
  type: string; // tslint:disable-line no-reserved-keywords
  order: number;
  colors: Object;
  story: string;
  entity_id: string;
}

export interface INewStoryContent {
  title: string;
  content: string;
  content_blocks?: IContentBlock[];
  layout?: string;
  group: string;
  format: string;
  type: string; // tslint:disable-line no-reserved-keywords
  order: number;
  active?: boolean;
  description?: string;
  thumbnail?: any;
  contentChartConfig?: IProgressTracker[];
}

export interface IStoryUpdateRequestBody {
  embeds?: any[];
  title?: string;
  description?: string;
  thumbnail?: any;
  thumbnail_relative_path?: string;
  meta?: any; // object
  visibility?: 'private' | 'organization' | 'network';
  state?: 'draft' | 'published' | 'archived';
}

export interface IStoryContent {
  _id: string;
  author: IUser;
  createdAt: string;
  createdBy: string | IUser;
  entity_id?: number;
  entityId?: number;
  title: string;
  content: string;
  contentHtml?: string;
  content_blocks?: IContentBlock[];
  layout: string;
  group: string;
  format: string;
  type: string; // tslint:disable-line no-reserved-keywords
  order: number;
  active: boolean;
  story: string | Partial<IStory>;
  updatedAt?: string;
  updatedBy?: string | IUser;
  description?: string;
  shouldClear?: boolean;
  shouldUpdate?: boolean;
  scrollTo?: boolean;
  mediaIndex?: number;
  storyId?: string;
  contentChartConfig?: IProgressTracker[];
}

export interface IUpdatePayload {
  storyId: string;
  embedId: string;
  type?: string;
  isReportEmbedListSelected?: boolean;
  publicationId?: string;
  controller?: AbortController;
}

export interface IStoryWithContent extends IStory {
  contents: IStoryContent[];
}

export interface IStoryMeta {
  storyContent: IStoryContent;
  storyContentOrder: number;
  storyContentInView: string;
}

export interface IStoryPadding {
  paddingTop?: number;
  paddingBottom?: number;
}

export interface IStoryMargins {
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
}

// Custom colors for each section of a story
export interface IStoryCustomColors {
  header: {
    background: string[];
    font: string[];
  };
  body_content: {
    background: string[];
    font: string[];
  };
  footer: {
    background: string[];
    font: string[];
  };
}

export interface IContentBlock {
  [index: number]: IContentElement;
}

export interface IContentElement {
  alt?: string;
  creationParameters?: Object;
  dataProviderId?: string;
  editable?: boolean;
  height?: string;
  hyperlink?: string;
  src?: string;
  text_content?: string;
  type: EContentElementType;
  width?: string;
}

export enum EContentElementType {
  EMBED = 'embed',
  TEXT = 'text',
  VIDEO = 'video',
  IMAGE = 'image'
}

export interface IPublication {
  id: string;
  title: string;
  entityId: number;
  description?: string;
  createdAt: string;
  createdBy: string | IUser;
  updatedAt: string;
  updatedBy: string;
  publicUrl?: string;
  publishedAt?: string;
  publishedBy?: string | IUser;
  thumbnailPath?: string;
  status: IPublicationState;
  customCssCode?: string;
  createdByUser: {
    firstName: string;
    lastName: string;
    _id: string;
    opengovId: number;
    email: string;
    title?: string;
  };
  setTocAsLandingPage: boolean;
  foldersCount?: { aggregate: { count: number } };
  orgId?: string | null;
}

export interface IColorOption {
  colorCode: string;
  selected?: boolean;
}

export interface IMediaLink {
  id: string;
  link: string;
  type: string;
  title: string;
}

export interface IPublicationNavMenuStory {
  id: string;
  menuId: string;
  displayName: string;
  order: number;
  storyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  story: {
    _id: string;
    title: string;
  };
}

export interface IPublicationNavMenuItem {
  id: string;
  publicationId: string;
  title: string;
  order: number;
  createdAt: string;
  createdBy: IUser;
  updatedAt: string;
  publicationNavigationMenuStories?: IPublicationNavMenuStory[];
}

export interface IPublicationBrandings {
  id: string;
  publicationId: string;
  idleBackgroundColor: IColorOption[];
  activeBackgroundColor: IColorOption[];
  activeFontColor: IColorOption[];
  enableLogoNavigation: boolean;
  fontStyle: string;
  idleFontColor: IColorOption[];
  isNavigationMenuSticky: boolean;
  logoUrl: string | null;
  showLogoOnFooter: boolean;
  createdAt: string;
  updatedAt: string;
  enableGoogleTranslate: boolean;
}

export interface IPublicationFooterSettings {
  id: string;
  publicationId: string;
  mediaLinks: IMediaLink[];
  backgroundColors: IColorOption[];
  fontColors: IColorOption[];
  createdBy: string;
}

export interface IPublicationTocSettings {
  id: string;
  columnLayoutNumber: number;
  createdBy: string;
  headerHeightSize: string | null;
  headerImageUrl: string | null;
  menuTitle: string | null;
  pageTitle: string | null;
  showTableOfContents: boolean;
  createdAt: string;
  publicationId: string;
  updatedAt: string;
  description: string | null;
  hasUserToggledToc: boolean;
}

export interface IPublicationFolderStory {
  id: string;
  title: string;
  publicUrl: string;
  customCssCode?: string;
}

export interface IPublicationFolderWithStories {
  id: string;
  title: string;
  stories: IPublicationFolderStory[];
  subFolders?: IPublicationFolderWithStories[];
}

export interface IReportEmbedDetails {
  embedData: {
    reportId: string;
    savedViewId: string;
    reportName: string;
    savedViewName: string;
    type: string;
    frequency: string;
  };
}

export interface IEmbedDetails {
  embedData: {
    report_id: string;
    saved_view_id: string;
    created_at: string;
    savedViewName: string;
    type: string;
    updated_at: string;
  }[];
}

export interface IBudgetProposals {
  id: string;
  proposalId: number;
  budgetId: number;
  story?: {
    entityId: number;
  };
}

export interface IBudgetPreferences {
  presentation_scale?: {
    amount?: number;
  };
}

export interface IBudget {
  budgetId: number;
  budgetTitle: string;
  proposals: IBudgetProposals[];
  preferences: IBudgetPreferences;
}

export interface IProposal {
  id: number;
  uuid: string;
  title: string;
  created_at: string;
  budget_id: number;
  status: string;
  updated_at: string;
  abilities: {
    destroy: boolean;
    read: boolean;
  };
}

export enum AsyncJobStatus {
  Enqueued = 'enqueued',
  InProgress = 'in_progress',
  Success = 'success',
  Error = 'error'
}

export enum TimelineChartOrientation {
  HORIZONTAL = 'horizontal',
  VERTICAL = 'vertical'
}

export interface IProposalDetails {
  id: number;
  budgetId: number;
  uuid: string;
  title: string;
  proposalFields: IFormField[];
}

export interface IAllProposalDetails {
  [key: string]: IProposalDetails | null;
}

export interface IStoryBudget {
  id: number;
  title: string;
}

export interface IStoryMergeTag {
  id: string;
  htmlId: string;
  value: string;
  inactive: boolean;
}

export interface IStoryMergeTags {
  [mergeTagHtmlId: string]: IStoryMergeTag;
}

export interface IMergeTag {
  id: string;
  proposalId: number;
  fieldId: string;
  value: string;
  label: string;
  description: string;
  content: string | null;
}

export interface IMergeTags {
  [mergeTagId: string]: IMergeTag;
}

// This represents the data returned from GraphQL and stored in storyList state
export interface IStoryFolder {
  id: string;
  title: string;
  order: number;
  parentFolderId: string;
  subFolders: IStoryFolder[];
  publication?: {
    id: string;
    title: string;
  };
  stories?: IStory[];
}

type PublicationRecord = {
  title: string;
  id: string;
};

type PublicationData = {
  publications: PublicationRecord[];
};

export type PublicationRecords = {
  data: PublicationData;
};

export type ClaimsResponse = {
  allClaims: string[];
  sectionIds?: string[] | 'All';
  subjectGroupUuids?: string[];
};
export type EventStatusType = {
  id: string;
  title: string;
}
export type ProgressTrackerSettingType = {
  id: string;
  status: string;
  colorCode: string;
  entityId: number;
  verticalLabelPosition: 'left' | 'right';
  horizontalLabelPosition: 'top' | 'bottom';
}
export type IProgressTrackerConfig = {
  showPreview: boolean;
  orientation: TimelineChartOrientation;
  title: string;
  events: {
    title: string;
    description: string;
    status: {
      id: string;
      title: string;
    };
  }[];
  progressTrackerSettings?: ProgressTrackerSettingType[];
}

export interface IProgressTracker {
  storyId: string;
  config: IProgressTrackerConfig;
  contentId: string;
  id?: string;
  type: string;
}

export interface IProgressTrackerPayload {
  storyId: string;
  config: IProgressTrackerConfig;
  contentId: string;
  entityId: number;
  type: string;
  id?: string;
}

export type ContainerData = {
  id: string;
  type: ContainerTypes;
  data?: IProgressTrackerConfig;   // Add other types for future containers
}

export enum ContainerTypes {
  FOLLOW_CONTAINER = 'follow-container',
  PROGRESS_TRACKER_CONTAINER = 'progress-tracker-container'
}

export type StoriesCommunityContainer = {
  id: string,
  type: string,
  data?: unknown
}

export type FormattedKafkaMessage = {
  data: {
    bgJob: {
      id: string;
      status: AsyncJobStatus;
    };
  };
  specversion: string;
  type: string;
  source: string;
  id: string;
  time: Date;
  datacontenttype: string;
  clientOptions?: RequestInit;
}
